'use client';

import { useCallback } from 'react';
import type { BugReport } from '../context/types';

interface Props {
  report: BugReport;
  onSubmitToGitHub?: () => void;
}

const SEVERITY_STYLES: Record<BugReport['severity'], string> = {
  critical: 'bg-negative/20 text-negative border border-negative/30',
  major: 'bg-warning/20 text-warning border border-warning/30',
  minor: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
  cosmetic: 'bg-surface-2 text-text-tertiary border border-stroke',
};

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString();
}

function buildMarkdown(report: BugReport): string {
  const lines: string[] = [];

  lines.push(`## Bug Report: ${report.title}`);
  lines.push(`**Severity:** ${report.severity}`);
  lines.push('');
  lines.push(`### Description`);
  lines.push(report.description);
  lines.push('');
  lines.push(`### Reproduction Steps`);
  report.reproductionSteps.forEach((step, i) => {
    lines.push(`${i + 1}. ${step}`);
  });
  lines.push('');
  lines.push(`### Expected Behavior`);
  lines.push(report.expectedBehavior);
  lines.push('');
  lines.push(`### Actual Behavior`);
  lines.push(report.actualBehavior);
  lines.push('');

  if (report.capturedErrors.length > 0) {
    lines.push(`### Captured Errors`);
    report.capturedErrors.forEach((err) => {
      lines.push(`\`\`\``);
      lines.push(err.message);
      if (err.stack) lines.push(err.stack);
      lines.push(`\`\`\``);
    });
    lines.push('');
  }

  if (report.capturedNetworkFailures.length > 0) {
    lines.push(`### Network Failures`);
    report.capturedNetworkFailures.forEach((req) => {
      lines.push(
        `- \`${req.method} ${req.url}\` → status: ${req.status ?? 'no response'} (${req.durationMs}ms)`,
      );
      if (req.error) lines.push(`  Error: ${req.error}`);
    });
    lines.push('');
  }

  lines.push(`### Environment`);
  lines.push(`- Build: ${report.environment.buildId}`);
  lines.push(`- Data env: ${report.environment.dataEnv}`);
  lines.push(`- User agent: ${report.environment.userAgent}`);
  lines.push(`- Route: ${report.appStateAtTime.currentRoute}`);
  lines.push(`- Wallet: ${report.appStateAtTime.walletAddress ?? 'not connected'}`);

  return lines.join('\n');
}

export function BugReportView({ report, onSubmitToGitHub }: Props) {
  const handleCopyMarkdown = useCallback(async () => {
    const md = buildMarkdown(report);
    try {
      await navigator.clipboard.writeText(md);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = md;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }, [report]);

  return (
    <div className="border-stroke flex flex-col gap-4 overflow-y-auto rounded-xl border p-4 text-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-text-primary font-semibold leading-snug">
          {report.title}
        </h3>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_STYLES[report.severity]}`}
        >
          {report.severity}
        </span>
      </div>

      {/* Description */}
      <div>
        <p className="text-text-secondary mb-1 text-xs font-medium uppercase tracking-wide">
          Description
        </p>
        <p className="text-text-primary text-sm">{report.description}</p>
      </div>

      {/* Reproduction Steps */}
      <div>
        <p className="text-text-secondary mb-1 text-xs font-medium uppercase tracking-wide">
          Reproduction Steps
        </p>
        <ol className="text-text-primary list-decimal pl-4 text-sm space-y-1">
          {report.reproductionSteps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </div>

      {/* Expected vs Actual */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-text-secondary mb-1 text-xs font-medium uppercase tracking-wide">
            Expected
          </p>
          <p className="text-text-primary text-xs">{report.expectedBehavior}</p>
        </div>
        <div>
          <p className="text-text-secondary mb-1 text-xs font-medium uppercase tracking-wide">
            Actual
          </p>
          <p className="text-negative text-xs">{report.actualBehavior}</p>
        </div>
      </div>

      {/* Captured Errors */}
      {report.capturedErrors.length > 0 && (
        <div>
          <p className="text-text-secondary mb-1 text-xs font-medium uppercase tracking-wide">
            Captured Errors ({report.capturedErrors.length})
          </p>
          <div className="space-y-2">
            {report.capturedErrors.map((err, i) => (
              <pre
                key={i}
                className="bg-surface-card border-negative/20 overflow-x-auto rounded-lg border p-2 font-mono text-xs text-red-400"
              >
                {err.message}
                {err.stack ? `\n${err.stack}` : ''}
              </pre>
            ))}
          </div>
        </div>
      )}

      {/* Network Failures */}
      {report.capturedNetworkFailures.length > 0 && (
        <div>
          <p className="text-text-secondary mb-1 text-xs font-medium uppercase tracking-wide">
            Network Failures ({report.capturedNetworkFailures.length})
          </p>
          <div className="space-y-1">
            {report.capturedNetworkFailures.map((req, i) => (
              <div
                key={i}
                className="bg-surface-card border-stroke flex items-start justify-between gap-2 rounded-lg border p-2 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <span className="text-text-secondary font-mono">
                    {req.method}
                  </span>{' '}
                  <span className="text-text-primary truncate font-mono">
                    {req.url}
                  </span>
                  {req.error && (
                    <p className="text-negative mt-0.5">{req.error}</p>
                  )}
                </div>
                <span
                  className={`shrink-0 font-mono ${req.status && req.status >= 400 ? 'text-negative' : req.status === null ? 'text-warning' : 'text-text-secondary'}`}
                >
                  {req.status ?? 'no resp'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Environment */}
      <div>
        <p className="text-text-secondary mb-1 text-xs font-medium uppercase tracking-wide">
          Environment
        </p>
        <div className="text-text-secondary space-y-0.5 text-xs">
          <p>
            Build: <span className="text-text-primary">{report.environment.buildId}</span>
          </p>
          <p>
            Network: <span className="text-text-primary">{report.environment.dataEnv}</span>
          </p>
          <p>
            Route: <span className="text-text-primary">{report.appStateAtTime.currentRoute}</span>
          </p>
          <p>
            Wallet:{' '}
            <span className="text-text-primary">
              {report.appStateAtTime.walletAddress ?? 'not connected'}
            </span>
          </p>
          <p className="truncate">
            UA:{' '}
            <span className="text-text-primary">
              {report.environment.userAgent}
            </span>
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-1">
        {onSubmitToGitHub && (
          <button
            type="button"
            onClick={onSubmitToGitHub}
            className="bg-accent hover:bg-accent/80 flex-1 rounded-lg py-2 text-xs font-medium text-white transition-colors"
          >
            Submit to GitHub
          </button>
        )}
        <button
          type="button"
          onClick={handleCopyMarkdown}
          className="bg-surface-2 text-text-secondary hover:text-text-primary flex-1 rounded-lg py-2 text-xs font-medium transition-colors"
        >
          Copy as Markdown
        </button>
      </div>
    </div>
  );
}
